import { getLogger } from '@logtape/logtape';
import { useState } from 'react';

import { COMMUNICATION_ERROR_TEXT_BY_STATUS, COMMUNICATION_SAVE_FAILED_TEXT } from '@/constants/communication.ts';

import { ApiError } from '@/lib/api-client.ts';

import { CommunicationContext } from '@/models/communication-context.ts';
import { PendingAction } from '@/models/communication-pending-action.ts';

import { type CreateDcCommunicationRequest } from '@/schemas/dc-communication.schema.ts';

import { createDcCommunication } from '@/services/dc-communication-service.ts';
import { showErrorNotification, showSuccessNotification } from '@/services/notificationService.ts';

import { serializeApiError } from '@/utils/log-utils.ts';

const logger = getLogger(['hook', 'communication-submit']);

interface UseCommunicationSubmitReturn {
    submit: (action: PendingAction, content: string) => Promise<boolean>;
    pendingAction: PendingAction | undefined;
    isSubmitting: boolean;
}

const toErrorMessage = (error: unknown): string =>
    (error instanceof ApiError ? COMMUNICATION_ERROR_TEXT_BY_STATUS[error.status] : undefined) ??
    COMMUNICATION_SAVE_FAILED_TEXT;

export const useCommunicationSubmit = (context: CommunicationContext): UseCommunicationSubmitReturn => {
    const [pendingAction, setPendingAction] = useState<PendingAction | undefined>(undefined);

    const submit = async (action: PendingAction, content: string): Promise<boolean> => {
        setPendingAction(action);

        const body: CreateDcCommunicationRequest = {
            patient_id: context.patientId,
            content: content,
            dc_calendar_id: context.dcCalendarId,
            insurance_type: context.insuranceType,
            date: context.date,
        };

        try {
            await createDcCommunication(context.instanceId, body);
            showSuccessNotification('Kommunikation in Insuite gespeichert.');
            setPendingAction(undefined);
            return true;
        } catch (error) {
            logger.error('Failed to create communication', serializeApiError(error));
            showErrorNotification(toErrorMessage(error));
            setPendingAction(undefined);
            return false;
        }
    };

    return { submit: submit, pendingAction: pendingAction, isSubmitting: pendingAction !== undefined };
};
